/**
 * Bridge module: adapts glitchedit effects (which operate on PNG raw data
 * with per-scanline filter bytes) to unstaglitch's canvas ImageData format
 * (RGBA, 4 bytes per pixel, no filter bytes).
 *
 * Each glitchedit effect has the signature:
 *   apply(pixelData, imageInfo, params, seed) => Uint8Array
 * where pixelData includes a leading filter byte per scanline and
 * imageInfo = { width, height, bytesPerPixel, scanlineLength }.
 *
 * This bridge converts canvas ImageData <-> glitchedit pixelData format,
 * maps positional args to named params, and returns functions with the
 * unstaglitch signature: (canvas, ctx, ...args) => Promise<void>.
 */

const {
    effectRegistry,
    getDefaultParams
} = require('./glitchedit-effects');

// Convert canvas ImageData (RGBA, no filter bytes) to glitchedit pixelData
// (with a leading 0 filter byte per scanline).
function imageDataToGlitchFormat(imageData) {
    const { width, height, data } = imageData;
    const bytesPerPixel = 4;
    const scanlineLength = width * bytesPerPixel + 1; // +1 for filter byte
    const pixelData = new Uint8Array(height * scanlineLength);
    for (let y = 0; y < height; y++) {
        pixelData[y * scanlineLength] = 0; // filter byte = None
        const srcStart = y * width * bytesPerPixel;
        const dstStart = y * scanlineLength + 1;
        pixelData.set(data.subarray(srcStart, srcStart + width * bytesPerPixel), dstStart);
    }
    return {
        pixelData,
        imageInfo: { width, height, bytesPerPixel, scanlineLength }
    };
}

// Convert glitchedit pixelData back to canvas ImageData (strip filter bytes).
function glitchFormatToImageData(pixelData, width, height) {
    const bytesPerPixel = 4;
    const scanlineLength = width * bytesPerPixel + 1;
    const out = new Uint8ClampedArray(width * height * bytesPerPixel);
    for (let y = 0; y < height; y++) {
        const srcStart = y * scanlineLength + 1; // skip filter byte
        const dstStart = y * width * bytesPerPixel;
        out.set(pixelData.subarray(srcStart, srcStart + width * bytesPerPixel), dstStart);
    }
    return new ImageData(out, width, height);
}

// Build a params object from positional args, using the effect's parameter list.
// Args from the UI come as strings — convert to numbers for slider/checkbox
// params, and to the option value string for dropdown params.
function buildParams(effect, args) {
    const params = {};
    const defaults = getDefaultParams(effect.id);
    effect.parameters.forEach((param, i) => {
        let val;
        if (i < args.length && args[i] !== undefined && args[i] !== null) {
            val = args[i];
        } else {
            val = defaults[param.id];
        }
        // Convert string args to proper types
        if (param.type === 'slider' || param.type === 'checkbox') {
            val = Number(val);
        } else if (param.type === 'dropdown') {
            // The knob sends an integer index — convert to the option value string
            if (typeof val === 'number' || /^\d+$/.test(String(val))) {
                const idx = Math.round(Number(val));
                val = param.options[idx]?.value ?? param.default;
            }
        }
        params[param.id] = val;
    });
    return params;
}

// Create a bridged function for a single glitchedit effect.
// Returns a function (canvas, ctx, ...args) => Promise<void>.
function createBridge(effect) {
    return async (canvas, ctx, ...args) => {
        const width = canvas.width;
        const height = canvas.height;
        const imageData = ctx.getImageData(0, 0, width, height);
        const { pixelData, imageInfo } = imageDataToGlitchFormat(imageData);
        const params = buildParams(effect, args);
        const seed = (Math.random() * 0xFFFFFFFF) >>> 0;
        const result = effect.apply(pixelData, imageInfo, params, seed);
        const newImageData = glitchFormatToImageData(result, width, height);
        ctx.putImageData(newImageData, 0, 0);
    };
}

// Build bridged functions for all registered effects.
// Returns a map: effectId -> bridged function.
function buildBridgedEffects() {
    const bridged = {};
    for (const [id, effect] of effectRegistry) {
        bridged[id] = createBridge(effect);
    }
    return bridged;
}

// Build a fx.js-compatible descriptor array from all glitchedit effects.
// Each entry: { name, inputs: [{name, type, min, max, step, value}], fn }
function buildFxDescriptors(bridgedEffects) {
    const descriptors = [];
    for (const [id, effect] of effectRegistry) {
        // Skip filter-byte effect — it manipulates PNG filter bytes which
        // don't exist in canvas ImageData; it would be a no-op.
        if (id === 'filter-byte') continue;

        const inputs = effect.parameters.map(param => {
            // Map glitchedit param types to unstaglitch 'knob' type
            if (param.type === 'checkbox') {
                return {
                    name: param.label || param.id,
                    type: 'knob',
                    min: 0,
                    max: 1,
                    step: 1,
                    value: param.default ? 1 : 0
                };
            }
            if (param.type === 'dropdown') {
                // Dropdowns use a numeric knob where the value is the option index
                const defaultIdx = Math.max(0, param.options.findIndex(o => o.value === param.default));
                return {
                    name: param.label || param.id,
                    type: 'knob',
                    min: 0,
                    max: param.options.length - 1,
                    step: 1,
                    value: defaultIdx
                };
            }
            // slider: numeric knob
            const min = param.min !== undefined ? param.min : 0;
            const max = param.max !== undefined ? param.max : 100;
            const step = param.step !== undefined ? param.step : 1;
            return {
                name: param.label || param.id,
                type: 'knob',
                min,
                max,
                step,
                value: param.default
            };
        });

        descriptors.push({
            name: 'ge-' + id,
            inputs,
            fn: bridgedEffects[id]
        });
    }
    return descriptors;
}

// List of all glitchedit effect IDs (for building fxNameMap in the worker).
function getEffectIds() {
    return Array.from(effectRegistry.keys()).filter(id => id !== 'filter-byte');
}

module.exports = {
    buildBridgedEffects,
    buildFxDescriptors,
    createBridge,
    imageDataToGlitchFormat,
    glitchFormatToImageData,
    buildParams,
    getEffectIds
};
